<template>
  <b-card no-body class="mb-1">
    <b-card-body class="py-1">
      <div class="d-flex flex-wrap align-items-center mb-50">
        <b-button
          v-if="item.artist.track"
          variant="flat-primary"
          size="sm"
          class="btn-icon mr-50"
          :title="isTrackPlaying(playerKey(item.artist.track)) ? 'Остановить' : 'Прослушать'"
          @click="playTrack(item.artist.track, item.artist.title)"
        >
          <feather-icon :icon="isTrackPlaying(playerKey(item.artist.track)) ? 'PauseIcon' : 'PlayIcon'" size="14" />
        </b-button>
        <router-link
          :to="{ name: 'artist-view', params: { id: item.artist.id } }"
          target="_blank"
          class="h5 mb-0 mr-1"
        >
          {{ item.artist.title }}
        </router-link>
        <b-badge v-if="item.artist.country" variant="light-secondary" class="mr-50">{{ item.artist.country }}</b-badge>
        <b-badge v-if="item.artist.gender" variant="light-secondary" class="mr-50">{{ item.artist.gender }}</b-badge>
        <b-badge v-if="item.artist.artist_type" variant="light-secondary" class="mr-50">{{ item.artist.artist_type }}</b-badge>
        <b-badge v-if="item.artist.decades && item.artist.decades.length" variant="light-info" class="mr-50">
          {{ item.artist.decades.join(', ') }}
        </b-badge>
        <b-badge v-if="item.artist.main_cluster" variant="light-primary" class="mr-50">{{ item.artist.main_cluster }}</b-badge>
        <div class="ml-auto">
          <b-button variant="flat-secondary" size="sm" class="mr-50" :disabled="isSaving" @click="$emit('skipped', item.artist.id)">
            Пропустить
          </b-button>
          <b-button v-if="$can('manage', 'db-music')" variant="primary" size="sm" :disabled="isSaving" @click="save">
            <b-spinner v-if="isSaving" small class="mr-50" />
            Готово
          </b-button>
        </div>
      </div>

      <div v-if="errorMessage" class="text-danger font-small-3 mb-50">{{ errorMessage }}</div>

      <div class="d-flex flex-wrap">
        <!-- Уже сохранённые похожие: клик снимает связь (зачёркивает) -->
        <span
          v-for="(row, rowIndex) in item.existing"
          :key="'e-' + rowIndex"
          class="similar-pill similar-pill--green"
          :class="{
            'similar-pill--removed': isRemoved(row),
            'similar-pill--static': row.similar_artist_id === null || !$can('manage', 'db-music'),
          }"
          @click="$can('manage', 'db-music') && toggleExisting(row)"
        >
          <b-button
            v-if="row.track"
            variant="flat-light"
            size="sm"
            class="btn-icon similar-pill__play"
            @click.stop="playTrack(row.track, row.title)"
          >
            <feather-icon :icon="isTrackPlaying(playerKey(row.track)) ? 'PauseIcon' : 'PlayIcon'" size="12" />
          </b-button>
          <span v-if="!isRemoved(row)" class="similar-pill__number">{{ existingNumber(row) }}</span>
          <span class="similar-pill__title">{{ row.title }}</span>
        </span>

        <!-- Кандидаты: порядок кликов = ранг -->
        <span
          v-for="candidate in item.candidates"
          :key="'c-' + candidate.id"
          class="similar-pill"
          :class="[candidatePillClass(candidate), { 'similar-pill--static': !$can('manage', 'db-music') }]"
          @click="$can('manage', 'db-music') && toggleCandidate(candidate.id)"
        >
          <b-button
            v-if="candidate.track"
            variant="flat-secondary"
            size="sm"
            class="btn-icon similar-pill__play"
            @click.stop="playTrack(candidate.track, candidate.title)"
          >
            <feather-icon :icon="isTrackPlaying(playerKey(candidate.track)) ? 'PauseIcon' : 'PlayIcon'" size="12" />
          </b-button>
          <span v-if="orderIndex(candidate.id) !== -1" class="similar-pill__number">{{ candidateNumber(candidate.id) }}</span>
          <span class="similar-pill__title">{{ candidate.title }}</span>
          <small v-if="candidate.country" class="similar-pill__meta">{{ candidate.country }}</small>
          <small
            v-if="candidate.gender"
            class="similar-pill__meta"
            :class="{ 'similar-pill__meta--mismatch': isGenderMismatch(candidate) }"
            :title="isGenderMismatch(candidate) ? 'Пол отличается от артиста' : null"
          >{{ genderLabel(candidate.gender) }}</small>
          <small
            v-if="candidate.decades && candidate.decades.length"
            class="similar-pill__meta"
            :class="{ 'similar-pill__meta--mismatch': isDecadesMismatch(candidate) }"
            :title="isDecadesMismatch(candidate) ? 'Нет общих десятилетий с артистом' : null"
          >{{ candidate.decades.join(', ') }}</small>
          <small
            v-for="source in (candidate.sources || [])"
            :key="source"
            class="similar-pill__source"
            :title="sourceLabel(source).title"
          >{{ sourceLabel(source).short }}</small>
        </span>
        <b-button
          v-if="item.has_more_candidates"
          variant="flat-primary"
          size="sm"
          class="mb-50"
          :disabled="isLoadingMore"
          @click="loadMoreCandidates"
        >
          <b-spinner v-if="isLoadingMore" small class="mr-50" />
          Ещё {{ morePageSize }}
        </b-button>
        <span v-if="!item.candidates.length && !item.existing.length" class="text-muted">Кандидатов нет</span>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BBadge, BSpinner } from 'bootstrap-vue'
import { ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { isSubmissionResponse, notifySubmitted } from '@/libs/moderation'
import { toggleTrack, isTrackPlaying } from '@/layouts/components/globalPlayer'

// Модуль стора регистрирует SimilarFeed.vue
const STORE_MODULE_NAME = 'similarFeed'

export default {
  components: {
    BCard,
    BCardBody,
    BButton,
    BBadge,
    BSpinner,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast()

    // id кандидатов «похож» в порядке кликов (порядок = ранг)
    const order = ref([])
    // id кандидатов «не похож»
    const rejected = ref([])
    // id существующих похожих, снятых кликом
    const removed = ref([])
    const isSaving = ref(false)
    const errorMessage = ref('')

    // Кандидат пропал из строки (пришёл обратной связью и переехал в «существующие») — забываем отметки по нему
    watch(() => props.item.candidates.map(candidate => candidate.id), candidateIds => {
      order.value = order.value.filter(id => candidateIds.includes(id))
      rejected.value = rejected.value.filter(id => candidateIds.includes(id))
    })

    const orderIndex = candidateId => order.value.indexOf(candidateId)
    const isRemoved = row => row.similar_artist_id !== null && removed.value.includes(row.similar_artist_id)

    const toggleCandidate = candidateId => {
      const index = orderIndex(candidateId)

      if (index !== -1) {
        order.value.splice(index, 1)
        rejected.value.push(candidateId)
      } else if (rejected.value.includes(candidateId)) {
        rejected.value = rejected.value.filter(id => id !== candidateId)
      } else {
        order.value.push(candidateId)
      }
    }

    // Свободные строки (similar_artist_id = null) не кликабельны, но занимают ранг
    const toggleExisting = row => {
      if (row.similar_artist_id === null) return

      if (isRemoved(row)) {
        removed.value = removed.value.filter(id => id !== row.similar_artist_id)
      } else {
        removed.value.push(row.similar_artist_id)
      }
    }

    // Номер оставшейся существующей строки: считаем неснятые строки до неё включительно
    const existingNumber = row => {
      let number = 0

      for (const existingRow of props.item.existing) {
        if (!isRemoved(existingRow)) number += 1
        if (existingRow === row) break
      }

      return number
    }

    const keptExistingCount = () => props.item.existing.filter(row => !isRemoved(row)).length
    const candidateNumber = candidateId => keptExistingCount() + orderIndex(candidateId) + 1

    const candidatePillClass = candidate => {
      if (orderIndex(candidate.id) !== -1) return 'similar-pill--green'
      if (rejected.value.includes(candidate.id)) return 'similar-pill--rejected'

      return ''
    }

    // Пол кандидата приходит из индекса в нижнем регистре («женский») — показываем коротко
    const GENDER_LABELS = { мужской: 'муж', женский: 'жен', другой: 'другой' }
    // Внешние источники связи кандидата: короткая метка на пилюле + полное название в title
    const SOURCE_LABELS = {
      yandex: { short: 'Я', title: 'Яндекс Музыка' },
      deezer: { short: 'Dz', title: 'Deezer' },
      listenbrainz: { short: 'LB', title: 'ListenBrainz' },
      lastfm: { short: 'FM', title: 'Last.fm' },
    }
    const sourceLabel = source => SOURCE_LABELS[source] || { short: source, title: source }
    const genderLabel = gender => GENDER_LABELS[String(gender).toLowerCase()] || gender

    // Подсветка расхождений с артистом строки: только когда атрибут известен у обоих
    const isGenderMismatch = candidate => Boolean(candidate.gender && props.item.artist.gender)
      && String(candidate.gender).toLowerCase() !== String(props.item.artist.gender).toLowerCase()
    const isDecadesMismatch = candidate => {
      const artistDecades = props.item.artist.decades || []
      const candidateDecades = candidate.decades || []
      if (!artistDecades.length || !candidateDecades.length) return false

      return !candidateDecades.some(decade => artistDecades.includes(decade))
    }

    // «Ещё N» кандидатов: сервер исключает уже показанных, строку дополняет родитель
    const morePageSize = 10
    const isLoadingMore = ref(false)
    const loadMoreCandidates = async () => {
      if (isLoadingMore.value) return

      isLoadingMore.value = true
      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/fetchMoreSimilarCandidates`, {
          id: props.item.artist.id,
          shownIds: props.item.candidates.map(candidate => candidate.id),
        })
        emit('candidates-loaded', {
          artistId: props.item.artist.id,
          candidates: response.data.candidates || [],
          hasMore: Boolean(response.data.has_more),
        })
      } catch (error) {
        showToast(getErrorText(error), 'danger', 'AlertTriangleIcon')
      } finally {
        isLoadingMore.value = false
      }
    }

    const playerKey = track => `sf-${track.id}`
    const playTrack = (track, artistTitle) => {
      toggleTrack({
        key: playerKey(track),
        url: track.file_url,
        title: track.title,
        artist: artistTitle,
      })
    }

    const showToast = (title, variant, icon) => toast({
      component: ToastificationContent,
      props: { title, icon, variant },
    })

    const getErrorText = error => {
      const data = error.response && error.response.data
      if (data && data.errors) {
        const firstField = Object.values(data.errors)[0]
        const firstMessage = Array.isArray(firstField) ? firstField[0] : firstField
        if (firstMessage) return firstMessage
      }

      return (data && data.message) || error.message || 'Произошла ошибка'
    }

    const save = async () => {
      if (isSaving.value) return

      isSaving.value = true
      errorMessage.value = ''

      // Непомеченные кандидаты считаются «не похожи»
      const untouchedIds = props.item.candidates
        .map(candidate => candidate.id)
        .filter(id => !order.value.includes(id) && !rejected.value.includes(id))

      const payload = {
        added: [...order.value],
        removed: [...removed.value],
        rejected: [...rejected.value, ...untouchedIds],
      }

      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/saveSimilarFeedRow`, {
          id: props.item.artist.id,
          payload,
        })

        if (isSubmissionResponse(response)) {
          notifySubmitted(toast)
        } else {
          showToast('Сохранено', 'success', 'CheckIcon')
        }
        // Админ: сервер вернул артистов, у которых появилась обратная связь (у заявки их нет)
        const reverseLinkedArtistIds = (!isSubmissionResponse(response) && response.data
          && response.data.reverse_linked_artist_ids) || []
        emit('saved', { artistId: props.item.artist.id, reverseLinkedArtistIds })
      } catch (error) {
        const text = getErrorText(error)
        errorMessage.value = text
        showToast(text, 'danger', 'AlertTriangleIcon')
      } finally {
        isSaving.value = false
      }
    }

    return {
      isSaving,
      errorMessage,
      orderIndex,
      isRemoved,
      toggleCandidate,
      toggleExisting,
      existingNumber,
      candidateNumber,
      candidatePillClass,
      genderLabel,
      isGenderMismatch,
      isDecadesMismatch,
      morePageSize,
      isLoadingMore,
      loadMoreCandidates,
      sourceLabel,
      playerKey,
      playTrack,
      isTrackPlaying,
      save,
    }
  },
}
</script>

<style scoped>
.similar-pill {
  display: inline-flex;
  align-items: center;
  margin: 0 0.5rem 0.5rem 0;
  padding: 0.2rem 0.7rem;
  border: 1px solid #d0d2d6;
  border-radius: 1rem;
  cursor: pointer;
  user-select: none;
}
.similar-pill--green {
  background: #28c76f;
  border-color: #28c76f;
  color: #fff;
}
.similar-pill--rejected {
  background: #f1f1f2;
  color: #b9b9c3;
  text-decoration: line-through;
}
.similar-pill--removed {
  background: #f1f1f2;
  border-color: #ea5455;
  color: #ea5455;
  text-decoration: line-through;
}
.similar-pill--static {
  cursor: default;
}
.similar-pill__number {
  font-weight: 700;
  margin-right: 0.4rem;
}
.similar-pill__meta {
  margin-left: 0.4rem;
  opacity: 0.7;
}
.similar-pill__meta--mismatch {
  color: #ea5455;
  font-weight: 700;
  opacity: 1;
}
.similar-pill--green .similar-pill__meta--mismatch {
  /* На зелёном фоне красный не читается — подчёркиваем */
  color: #fff;
  text-decoration: underline wavy;
}
.similar-pill__source {
  margin-left: 0.3rem;
  padding: 0 0.3rem;
  font-size: 0.65rem;
  font-weight: 700;
  border: 1px solid currentColor;
  border-radius: 0.3rem;
  opacity: 0.8;
}
.similar-pill__play {
  padding: 0.1rem;
  margin-right: 0.3rem;
}
</style>
